import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";
import { categoryMeta, searchContent, type SearchResult } from "@/lib/content";
import { newsList } from "@/mocks/news";
import { recommendList } from "@/mocks/recommend";
import type { CategoryType } from "@/types/content";

const typeFilters: { key: "all" | CategoryType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "news", label: "资讯" },
  { key: "recommend", label: "番剧" },
  { key: "character", label: "角色" },
];

const typeLabel: Record<CategoryType, string> = {
  news: "资讯",
  recommend: "番剧",
  character: "角色",
};

const typeStyle: Record<CategoryType, string> = {
  news: "bg-secondary-100 text-secondary-900",
  recommend: "bg-primary-100 text-accent-700",
  character: "bg-accent-100 text-accent-800",
};

function resultLink(result: SearchResult) {
  return `/detail/${result.type}/${result.id}`;
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";

  usePageMeta({
    title: keyword ? `搜索「${keyword}」 - 香蕉动漫` : "站内搜索 - 香蕉动漫",
    description: "在香蕉动漫搜索动漫资讯、番剧推荐与角色介绍，快速找到你感兴趣的动漫内容。",
    keywords: "香蕉动漫,站内搜索,动漫搜索,番剧搜索,角色搜索",
  });

  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState<"all" | CategoryType>("all");
  const [focused, setFocused] = useState(false);

  const results = useMemo(() => searchContent(keyword), [keyword]);
  const filtered = useMemo(
    () => (typeFilter === "all" ? results : results.filter((r) => r.type === typeFilter)),
    [results, typeFilter],
  );

  const suggestions = useMemo(
    () => (input.trim() ? searchContent(input).slice(0, 5) : []),
    [input],
  );

  const hotNews = newsList.slice(0, 3);
  const hotRecommends = recommendList.slice(0, 3);

  const submit = (value: string) => {
    const v = value.trim();
    setFocused(false);
    if (v) setSearchParams({ q: v });
    else setSearchParams({});
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: "站内搜索" }]} />

      <div className="mt-5 max-w-3xl">
        <h1 className="font-display font-black text-2xl md:text-3xl text-foreground-950">
          搜索动漫内容
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          支持按关键词检索动漫资讯、番剧推荐与角色介绍（当前为前端演示搜索，真实搜索功能待接入）。
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(input);
          }}
          className="mt-5 relative"
        >
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 pl-4 pr-1.5 py-1.5 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-foreground-500 text-lg w-5 h-5 flex items-center justify-center"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => window.setTimeout(() => setFocused(false), 150)}
              placeholder="输入番剧、角色或资讯关键词，如「星海纪元」"
              className="w-full bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none py-1.5"
            />
            <button
              type="submit"
              className="shrink-0 rounded-full bg-accent-500 px-5 py-2 text-sm font-bold text-white hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>

          {focused && suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-2 z-30 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              {suggestions.map((s) => (
                <button
                  key={`${s.type}-${s.id}`}
                  type="button"
                  onMouseDown={() => {
                    setInput(s.title);
                    submit(s.title);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${typeStyle[s.type]}`}>
                    {typeLabel[s.type]}
                  </span>
                  <span className="text-sm text-foreground-800 line-clamp-1">{s.title}</span>
                </button>
              ))}
            </div>
          )}
        </form>

        {keyword && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {typeFilters.map((f) => {
              const count =
                f.key === "all"
                  ? results.length
                  : results.filter((r) => r.type === f.key).length;
              const active = typeFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setTypeFilter(f.key)}
                  className={`rounded-full px-3.5 py-1.5 text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-primary-500 text-foreground-950 font-bold"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {f.label}（{count}）
                </button>
              );
            })}
          </div>
        )}
      </div>

      {keyword && (
        <div className="mt-6">
          <p className="text-sm text-foreground-500 mb-4">
            「<span className="font-bold text-foreground-900">{keyword}</span>」的搜索结果，共{" "}
            <span className="font-bold text-foreground-900">{filtered.length}</span> 条
          </p>

          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-emotion-sad-line"
              title="未找到相关内容"
              description="换个关键词试试吧，或者看看下面这些热门推荐。"
              onAction={() => {
                setInput("");
                setSearchParams({});
                setTypeFilter("all");
              }}
              actionLabel="清空搜索"
            />
          ) : (
            <div className="flex flex-col gap-3">
              {filtered.map((result) => (
                <Link
                  key={`${result.type}-${result.id}`}
                  to={resultLink(result)}
                  className="group flex flex-col sm:flex-row gap-4 rounded-lg border border-background-200 bg-background-50 p-3 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="shrink-0 w-full sm:w-40 h-36 sm:h-28 rounded-md overflow-hidden">
                    <img
                      src={result.cover}
                      alt={result.title}
                      title={`${result.title} - 香蕉动漫`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${typeStyle[result.type]}`}>
                        {typeLabel[result.type]}
                      </span>
                      <span className="text-xs text-foreground-400">
                        {categoryMeta[result.type].title}
                      </span>
                    </div>
                    <h3 className="font-bold text-sm md:text-base text-foreground-950 line-clamp-2 group-hover:text-accent-600 transition-colors">
                      {result.title}
                    </h3>
                    <p className="mt-1.5 text-xs md:text-sm text-foreground-600 line-clamp-2">
                      {result.summary}
                    </p>
                    <p className="mt-1.5 text-xs text-foreground-400">{result.meta}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {(!keyword || filtered.length === 0) && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section>
            <h2 className="font-display font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-fire-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
              热门资讯
            </h2>
            <div className="flex flex-col gap-3">
              {hotNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/news/${item.id}`}
                  className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="shrink-0 w-20 h-16 rounded-md overflow-hidden">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 香蕉动漫`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm text-foreground-900 line-clamp-2 group-hover:text-accent-600 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-400">{item.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-star-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
              热门番剧
            </h2>
            <div className="flex flex-col gap-3">
              {hotRecommends.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/recommend/${item.id}`}
                  className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="shrink-0 w-14 h-20 rounded-md overflow-hidden">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 香蕉动漫`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex flex-col justify-center">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-accent-600 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{item.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}